import React, { useState, useEffect } from 'react';
import { 
  Store, 
  Package,
  ShoppingCart,
  Clock,
  AlertTriangle,
  CheckCircle,
  RefreshCcw,
  Activity,
  Wallet
} from 'lucide-react';
import { shopService, ShopStatistics } from '../services/shopService';
import { customToast } from '../utils/customToast';
import { useTheme } from '../contexts/ThemeContext';
import { useTitle } from '../hooks/useTitle';
import { useCurrency } from '../hooks/useCurrency';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';

const AdminDashboard: React.FC = () => {
  const { theme } = useTheme();
  const { formatCurrency } = useCurrency();
  const [statistics, setStatistics] = useState<ShopStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Set page title
  useTitle('Admin Dashboard');

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const data = await shopService.getStatistics();
      setStatistics(data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
      customToast.error('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await fetchStatistics();
      customToast.success('Dashboard refreshed successfully');
    } catch (error) {
      console.error('Error refreshing dashboard:', error);
      customToast.error('Failed to refresh dashboard');
    } finally {
      setRefreshing(false);
    }
  };

  const stats = [
    {
      title: 'Total Shops',
      value: statistics?.total_shops || 0,
      icon: Store,
      color: 'blue',
      bgClass: theme === 'dark' ? 'bg-blue-900/20' : 'bg-blue-50',
      textClass: theme === 'dark' ? 'text-blue-400' : 'text-blue-600',
      iconBg: theme === 'dark' ? 'bg-blue-900/30' : 'bg-blue-100',
    },
    {
      title: 'Active Shops',
      value: statistics?.active_shops || 0,
      icon: CheckCircle,
      color: 'green',
      bgClass: theme === 'dark' ? 'bg-green-900/20' : 'bg-green-50',
      textClass: theme === 'dark' ? 'text-green-400' : 'text-green-600',
      iconBg: theme === 'dark' ? 'bg-green-900/30' : 'bg-green-100',
    },
    {
      title: 'Trial Shops',
      value: statistics?.trial_shops || 0,
      icon: Clock,
      color: 'yellow',
      bgClass: theme === 'dark' ? 'bg-yellow-900/20' : 'bg-yellow-50',
      textClass: theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600',
      iconBg: theme === 'dark' ? 'bg-yellow-900/30' : 'bg-yellow-100',
    },
    {
      title: 'Suspended Shops',
      value: statistics?.suspended_shops || 0,
      icon: AlertTriangle,
      color: 'red',
      bgClass: theme === 'dark' ? 'bg-red-900/20' : 'bg-red-50',
      textClass: theme === 'dark' ? 'text-red-400' : 'text-red-600',
      iconBg: theme === 'dark' ? 'bg-red-900/30' : 'bg-red-100',
    },
    {
      title: 'Platform Earnings',
      value: formatCurrency(statistics?.total_earnings || 0),
      icon: Wallet,
      color: 'purple',
      bgClass: theme === 'dark' ? 'bg-purple-900/20' : 'bg-purple-50',
      textClass: theme === 'dark' ? 'text-purple-400' : 'text-purple-600',
      iconBg: theme === 'dark' ? 'bg-purple-900/30' : 'bg-purple-100',
    },
    {
      title: 'All Shops Revenue',
      value: formatCurrency(statistics?.total_revenue || 0),
      icon: Activity,
      color: 'emerald',
      bgClass: theme === 'dark' ? 'bg-emerald-900/20' : 'bg-emerald-50',
      textClass: theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600',
      iconBg: theme === 'dark' ? 'bg-emerald-900/30' : 'bg-emerald-100',
    },
    {
      title: 'All Shops Products',
      value: statistics?.total_products || 0,
      icon: Package,
      color: 'indigo',
      bgClass: theme === 'dark' ? 'bg-indigo-900/20' : 'bg-indigo-50',
      textClass: theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600',
      iconBg: theme === 'dark' ? 'bg-indigo-900/30' : 'bg-indigo-100',
    },
    {
      title: 'All Shops Sales',
      value: statistics?.total_sales || 0,
      icon: ShoppingCart,
      color: 'pink',
      bgClass: theme === 'dark' ? 'bg-pink-900/20' : 'bg-pink-50',
      textClass: theme === 'dark' ? 'text-pink-400' : 'text-pink-600',
      iconBg: theme === 'dark' ? 'bg-pink-900/30' : 'bg-pink-100',
    },
  ];

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      trial: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      expired: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      suspended: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.suspended}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4 lg:space-y-6 p-2 sm:p-4 lg:p-0">
      {/* Header */}
      <div className="space-y-3 sm:space-y-4">
        <div>
          <h1 className={`text-xl sm:text-2xl lg:text-3xl font-bold ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            System Overview
          </h1>
          <p className={`mt-1 text-sm ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Monitor all shops and system-wide metrics
          </p>
        </div>
        
        {/* Controls */}
        <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:items-center sm:space-x-4">
            {/* Refresh button */}
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className={`flex items-center justify-center space-x-2 px-4 py-2.5 sm:py-1.5 rounded-lg border transition-all touch-manipulation min-h-[44px] sm:min-h-0 ${
                refreshing
                  ? 'opacity-50 cursor-not-allowed'
                  : theme === 'dark'
                    ? 'border-slate-600 text-gray-300 hover:border-slate-500 hover:bg-slate-700 active:bg-slate-600'
                    : 'border-gray-300 text-gray-600 hover:border-gray-400 hover:bg-gray-50 active:bg-gray-100'
              }`}
            >
              <RefreshCcw size={16} className={refreshing ? 'animate-spin' : ''} />
              <span className="text-sm font-medium">Refresh</span>
            </button>
          </div>

          {/* Last updated */}
          <div className={`hidden sm:flex items-center space-x-2 text-sm ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>
            <Activity size={16} />
            <span>Last updated: {format(new Date(), 'MMM dd, yyyy HH:mm')}</span>
          </div>
        </div>
      </div>

      {/* Statistics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div
              key={index}
              className={`p-3 sm:p-4 lg:p-6 rounded-2xl border transition-all duration-200 hover:shadow-lg touch-manipulation ${
                theme === 'dark'
                  ? 'bg-slate-800 border-slate-700 hover:border-slate-600'
                  : 'bg-white border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className={`text-xs sm:text-sm font-medium ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {stat.title}
                  </p>
                  <p className={`mt-1 sm:mt-2 text-xl sm:text-2xl font-bold truncate ${stat.textClass}`}>
                    {stat.value}
                  </p>
                </div>
                <div className={`flex-shrink-0 p-2 sm:p-3 rounded-xl ${stat.iconBg}`}>
                  <Icon className={`h-5 w-5 sm:h-6 sm:w-6 ${stat.textClass}`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent Shops */}
      <div className={`rounded-2xl border transition-all duration-200 ${
        theme === 'dark'
          ? 'bg-slate-800 border-slate-700'
          : 'bg-white border-gray-200'
      }`}>
        <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h2 className={`text-base sm:text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Recent Shops
              </h2>
              <p className={`mt-1 text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Latest shops added to the system
              </p>
            </div>
            <Link
              to="/admin/shops"
              className={`text-xs sm:text-sm font-medium px-3 py-1.5 rounded-lg transition-colors ${
                theme === 'dark' 
                  ? 'text-blue-400 hover:bg-slate-700' 
                  : 'text-blue-600 hover:bg-blue-50'
              }`}
            >
              View All →
            </Link>
          </div>
        </div>

        <div className="overflow-x-auto">
          {!statistics?.recent_shops || statistics.recent_shops.length === 0 ? (
            <div className="p-8 text-center">
              <Store className={`mx-auto h-12 w-12 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`} />
              <p className={`mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                No shops found
              </p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className={theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Shop
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Owner
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Status
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Created
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Stats
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {statistics.recent_shops.map((shop) => (
                  <tr key={shop.id} className={theme === 'dark' ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`flex-shrink-0 h-10 w-10 rounded-lg flex items-center justify-center ${
                          theme === 'dark' ? 'bg-blue-900/30' : 'bg-blue-100'
                        }`}>
                          <Store className={`h-5 w-5 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} />
                        </div>
                        <div className="ml-4">
                          <div className={`text-sm font-medium truncate max-w-48 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`} title={shop.shop_name}>
                            {shop.shop_name}
                          </div>
                          <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                            ID: #{shop.id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {shop.owner_name}
                      </div>
                      <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        {shop.owner_email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(shop.subscription_status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {format(new Date(shop.created_at), 'MMM dd, yyyy')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex gap-4 text-sm">
                        <div className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                          <span className="font-medium">{shop.users_count || 0}</span> Users
                        </div>
                        <div className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                          <span className="font-medium">{shop.products_count || 0}</span> Products
                        </div>
                        <div className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                          <span className="font-medium">{shop.sales_count || 0}</span> Sales
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
